import { Close as CloseIcon, KeyboardArrowDownRounded } from '@mui/icons-material';
import { Autocomplete, Chip, FormControl, TextField, Typography } from '@mui/material';
import type { FormikProps } from 'formik';
import type { FC } from 'react';
import type { Choice, Field } from '../QuestionEngine';

export interface MultiSelectChipsChoicesProps {
  field: Field;
  formik: FormikProps<Record<string, unknown>>;
  state: Record<string, unknown>;
  setState: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  isReadOnly?: boolean;
  separator?: string;
}

const MultiSelectChipsChoices: FC<MultiSelectChipsChoicesProps> = ({
  field,
  formik,
  state,
  setState,
  isReadOnly = false,
  separator = '|',
}) => {
  const options = field.choices ?? [];
  const valueStr = (state?.[field.code] ?? '') as string;
  const selectedOptions = valueStr
    ? valueStr
        .split(separator)
        .filter(Boolean)
        .map((code) => options.find((o) => o.code === code))
        .filter((o): o is Choice => Boolean(o))
    : [];

  const handleChange = (_: unknown, newValue: Choice[]) => {
    const newStr = newValue.map((o) => o.code).join(separator);
    setState((prev) => ({ ...prev, [field.code]: newStr }));
    formik.setFieldValue(field.code, newStr);
  };

  const fieldHasError =
    (formik.touched as Record<string, boolean>)?.[field.code] &&
    Boolean((formik.errors as Record<string, string>)?.[field.code]);
  const errorMessage =
    fieldHasError && ((formik.errors as Record<string, string>)?.[field.code] as string);

  return (
    <FormControl sx={{ width: '100%' }} error={fieldHasError}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {field.question_text ?? field.name}
        {!field.is_optional && ' *'}
      </Typography>
      <Autocomplete
        multiple
        filterSelectedOptions
        disabled={isReadOnly}
        disableClearable
        clearOnBlur
        popupIcon={<KeyboardArrowDownRounded />}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        renderInput={(params) => (
          <TextField
            {...params}
            id={field.code}
            name={field.code}
            error={fieldHasError}
            helperText={errorMessage}
          />
        )}
        renderTags={(value: readonly Choice[], getTagProps) =>
          value.map((option, index) => {
            const { key, onDelete, ...restTagProps } = getTagProps({ index });
            return (
              <Chip
                {...restTagProps}
                variant="outlined"
                label={option.name}
                key={key}
                deleteIcon={<CloseIcon />}
                onDelete={isReadOnly ? undefined : onDelete}
                sx={{
                  borderRadius: '4px',
                  borderColor: (theme) => theme.palette.customColors.lightGray,
                  backgroundColor: (theme) => theme.palette.customColors.lightBeige,
                  fontSize: '14px',
                  height: '24px',
                  lineHeight: '20px',
                  '& span': {
                    padding: '0 4px 0 8px',
                  },
                  '& .MuiChip-deleteIcon': {
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                    marginLeft: '0',
                    color: (theme) => theme.palette.customColors.cream,
                  },
                }}
              />
            );
          })
        }
        renderOption={(props, option) => (
          <li {...props} key={option.code}>
            {option.name}
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.code === value.code}
      />
    </FormControl>
  );
};

export default MultiSelectChipsChoices;
